import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getAuthInstance } from '../firebase';

const ProtectedRoute = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authInstance = await getAuthInstance();
        
        if (!authInstance) {
          setIsAuthorized(false);
          setLoading(false);
          return;
        }

        const unsubscribe = onAuthStateChanged(authInstance, async (currentUser) => {
          if (!currentUser) {
            setIsAuthorized(false);
            setUser(null);
            setLoading(false);
            return;
          }

          setUser(currentUser);

          try {
            // Force refresh token to get latest custom claims
            await currentUser.getIdToken(true);
            
            // Get token result to check role from custom claims
            const tokenResult = await currentUser.getIdTokenResult();
            const role = tokenResult.claims.role;
            
            const isBrokerOwner = tokenResult.claims.brokerOwner === true;
            // Allow active, admin, broker-only, and active users with broker ownership claim
            setIsAuthorized(
              role === 'active_user' ||
              role === 'admin_user' ||
              role === 'broker_owner' ||
              isBrokerOwner
            );
            setUserRole(role);
            setLoading(false);
            
          } catch (error) {
            console.error('Error checking role:', error);
            setIsAuthorized(false);
            setLoading(false);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error initializing auth:', error);
        setIsAuthorized(false);
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        fontSize: '1.2rem'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  if (!isAuthorized) {
    // User is authenticated but not active_user or admin_user - redirect to complete payment
    return <Navigate to="/complete-payment" replace />;
  }

  // Only admin users can access admin routes.
  if (userRole !== 'admin_user' && location.pathname.startsWith('/app/admin')) {
    return <Navigate to="/app/mls-gpt" replace />;
  }

  // Redirect admin users to admin dashboard if they're trying to access regular routes
  if (userRole === 'admin_user' && !location.pathname.startsWith('/app/admin')) {
    return <Navigate to="/app/admin" replace />;
  }

  // Broker-only users can only access their broker dashboard.
  if (userRole === 'broker_owner' && !location.pathname.startsWith('/app/broker')) {
    return <Navigate to="/app/broker" replace />;
  }

  return children;
};

export default ProtectedRoute; 